import React, { Suspense, lazy } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { detectLanguage, isSupportedLanguage, loadLanguage } from './i18n/config';

import ThemeModeProvider from './components/ThemeModeProvider';
import HomePage from './pages/HomePage';
import Header, { HEADER_HEIGHT } from './components/Header';
import Footer from './components/Footer';
import SEO from './components/SEO';
import ToolLayout from './components/ToolLayout';
import { GoogleAd } from './components/GoogleAd';
import { AD_SIZES } from './config/googleAds';
import { DEFAULT_LANGUAGE, getToolBySlug, pagePath } from './seo/seoData';

// Tools are code-split so the home page and each tool only load what they need.
const toolComponents: Record<string, React.LazyExoticComponent<React.ComponentType>> = {
  base64: lazy(() => import('./components/Base64Tool')),
  urlEncoder: lazy(() => import('./components/UrlEncoder')),
  jwt: lazy(() => import('./components/JWTTool')),
  hashGenerator: lazy(() => import('./components/HashGenerator')),
  htmlEntities: lazy(() => import('./components/HtmlEntities')),
  jsonBeautifier: lazy(() => import('./components/JsonBeautifier')),
  sqlFormatter: lazy(() => import('./components/SqlFormatter')),
  xmlBeautifier: lazy(() => import('./components/XmlBeautifier')),
  htmlBeautifier: lazy(() => import('./components/HtmlBeautifier')),
  jsonToClass: lazy(() => import('./components/JsonToClass')),
  jsonYaml: lazy(() => import('./components/JsonYaml')),
  jsonCsv: lazy(() => import('./components/JsonCsv')),
  timestampConverter: lazy(() => import('./components/TimestampConverter')),
  numberBaseConverter: lazy(() => import('./components/NumberBaseConverter')),
  colorConverter: lazy(() => import('./components/ColorConverter')),
  uuidGenerator: lazy(() => import('./components/UuidGenerator')),
  regexTester: lazy(() => import('./components/RegexTester')),
  cronParser: lazy(() => import('./components/CronParser')),
  textComparer: lazy(() => import('./components/TextComparer')),
  tcknVkn: lazy(() => import('./components/TcknVkn')),
  ibanValidator: lazy(() => import('./components/IbanValidator')),
  xsltViewer: lazy(() => import('./components/XsltViewer')),
};

const SIDE_AD_WIDTH = 160;

const SideAd = ({ side }: { side: 'left' | 'right' }) => (
  <Box
    component="aside"
    sx={{
      width: SIDE_AD_WIDTH,
      display: { xs: 'none', lg: 'flex' },
      alignItems: 'center',
      justifyContent: 'center',
      position: 'fixed',
      [side]: 0,
      top: HEADER_HEIGHT,
      bottom: 0,
    }}
  >
    {/* GoogleAd expects the AD_POSITIONS key; passing the value made both sides use the left slot. */}
    <GoogleAd
      position={side === 'left' ? 'LEFT_SIDEBAR' : 'RIGHT_SIDEBAR'}
      size={AD_SIZES.SKYSCRAPER as keyof typeof AD_SIZES}
    />
  </Box>
);

const ToolRoute = ({ lang }: { lang: string }) => {
  const { slug } = useParams();
  const tool = getToolBySlug(slug);

  if (!tool) {
    return <Navigate to={pagePath(lang)} replace />;
  }

  const ToolComponent = toolComponents[tool.id];

  return (
    <>
      <SEO lang={lang} toolId={tool.id} />
      <ToolLayout toolId={tool.id} lang={lang}>
        <Suspense
          fallback={
            <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          }
        >
          <ToolComponent />
        </Suspense>
      </ToolLayout>
    </>
  );
};

const LanguageRoute = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  React.useLayoutEffect(() => {
    if (isSupportedLanguage(lang) && i18n.language !== lang) {
      loadLanguage(lang);
    }
  }, [lang, i18n]);

  if (!isSupportedLanguage(lang)) {
    return <Navigate to={pagePath(DEFAULT_LANGUAGE)} replace />;
  }

  // Wait one tick for the language switch so we never render mixed-language content.
  if (i18n.language !== lang) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header lang={lang} />
      <SideAd side="left" />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: `${HEADER_HEIGHT}px`,
          mx: { lg: `${SIDE_AD_WIDTH}px` },
        }}
      >
        <Box component="main" sx={{ flexGrow: 1, py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
          <Routes>
            <Route
              index
              element={
                <>
                  <SEO lang={lang} />
                  <HomePage lang={lang} />
                </>
              }
            />
            <Route path=":slug" element={<ToolRoute lang={lang} />} />
            <Route path="*" element={<Navigate to={pagePath(lang)} replace />} />
          </Routes>
        </Box>
        <Footer lang={lang} />
      </Box>
      <SideAd side="right" />
    </Box>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeModeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Navigate to={pagePath(detectLanguage())} replace />} />
            <Route path="/:lang/*" element={<LanguageRoute />} />
          </Routes>
        </Router>
      </ThemeModeProvider>
    </HelmetProvider>
  );
}

export default App;
